import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Dumbbell, Star, ExternalLink, Send, CheckCircle, Filter, Search, Instagram, Globe, DollarSign, Calendar, ChevronDown, ChevronUp, Clock, Map, List, Navigation, Crosshair, Loader, GitCompare, X, Check, Minus, Share2, Link2 } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, useMap, Circle } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Haversine distance in miles
function haversine(lat1, lon1, lat2, lon2) {
  const R = 3959; // Earth radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Build Google Maps directions URL
function getDirectionsUrl(gym, userLocation) {
  const dest = encodeURIComponent(
    `${gym.address ? gym.address + ', ' : ''}${gym.city}, ${gym.state} ${gym.zip_code || ''}`.trim()
  );
  const origin = userLocation ? `&origin=${userLocation.lat},${userLocation.lng}` : '';
  return `https://www.google.com/maps/dir/?api=1${origin}&destination=${dest}&travelmode=driving`;
}

// User location marker
const userIcon = new L.DivIcon({
  className: '',
  html: `<div style="width:20px;height:20px;background:#3b82f6;border:3px solid #fff;border-radius:50%;box-shadow:0 0 12px rgba(59,130,246,0.6),0 0 24px rgba(59,130,246,0.3);"></div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

const AMENITY_OPTIONS = [
  'HYROX Simulation', 'SkiErg', 'Sled Push/Pull', 'Rowing Machines', 'Wall Balls',
  'Sandbag Carries', 'Burpee Broad Jump Area', 'Running Track', 'Farmers Carry',
  'Open Floor Space', 'Recovery Area', 'Showers', 'Parking', 'Personal Training'
];

const CLASS_OPTIONS = [
  'HYROX Prep', 'HYROX Simulation', 'Functional Fitness', 'CrossFit',
  'Strength & Conditioning', 'Running Club', 'Endurance Training', 'HIIT', 'Open Gym'
];

// Custom map marker icon
const gymIcon = new L.DivIcon({
  className: '',
  html: `<div style="width:32px;height:32px;background:#E63946;border:2px solid #fff;border-radius:50%;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(0,0,0,0.4);">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m6.5 6.5 11 11"/><path d="m21 21-1-1"/><path d="m3 3 1 1"/><path d="m18 22 4-4"/><path d="m2 6 4-4"/><path d="m3 10 7-7"/><path d="m14 21 7-7"/></svg>
  </div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -34],
});

const hyroxIcon = new L.DivIcon({
  className: '',
  html: `<div style="width:36px;height:36px;background:#c8ff00;border:2px solid #fff;border-radius:50%;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(0,0,0,0.4);">
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#000" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m6.5 6.5 11 11"/><path d="m21 21-1-1"/><path d="m3 3 1 1"/><path d="m18 22 4-4"/><path d="m2 6 4-4"/><path d="m3 10 7-7"/><path d="m14 21 7-7"/></svg>
  </div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 36],
  popupAnchor: [0, -38],
});

// Geocode cache to avoid repeated API calls
const geocodeCache = {};

async function geocodeAddress(gym) {
  const key = `${gym.address || ''} ${gym.city} ${gym.state} ${gym.zip_code || ''}`.trim();
  if (geocodeCache[key]) return geocodeCache[key];

  try {
    const q = encodeURIComponent(key);
    const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${q}&limit=1`, {
      headers: { 'User-Agent': 'NinjAthlete-GymDirectory/1.0' }
    });
    const data = await res.json();
    if (data.length > 0) {
      const coords = { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
      geocodeCache[key] = coords;
      return coords;
    }
  } catch { }
  return null;
}

// Auto-fit map bounds to markers
function FitBounds({ positions, userLocation }) {
  const map = useMap();
  useEffect(() => {
    const allPoints = [...positions.map(p => [p.lat, p.lng])];
    if (userLocation) allPoints.push([userLocation.lat, userLocation.lng]);
    if (allPoints.length > 0) {
      const bounds = L.latLngBounds(allPoints);
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
    }
  }, [positions, userLocation, map]);
  return null;
}

function GymMapView({ gyms, userLocation }) {
  const [markers, setMarkers] = useState([]);
  const [geocoding, setGeocoding] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function geocodeAll() {
      setGeocoding(true);
      const results = [];
      for (const gym of gyms) {
        if (cancelled) break;
        const coords = await geocodeAddress(gym);
        if (coords) results.push({ ...gym, coords });
        // Small delay to respect Nominatim rate limit (1 req/sec)
        await new Promise(r => setTimeout(r, 300));
      }
      if (!cancelled) {
        setMarkers(results);
        setGeocoding(false);
      }
    }
    if (gyms.length > 0) geocodeAll();
    else { setMarkers([]); setGeocoding(false); }
    return () => { cancelled = true; };
  }, [gyms]);

  const defaultCenter = [32.78, -96.80]; // Dallas, TX

  return (
    <div className="relative" data-testid="gym-map-view">
      {geocoding && markers.length === 0 && (
        <div className="absolute inset-0 z-[1000] bg-[#0a0a0a]/80 flex items-center justify-center">
          <div className="text-center">
            <Clock className="w-6 h-6 text-[#c8ff00] animate-spin mx-auto mb-2" />
            <p className="text-zinc-400 text-sm">Loading map locations...</p>
          </div>
        </div>
      )}
      <div className="border border-zinc-800 overflow-hidden" style={{ height: '520px' }}>
        <MapContainer
          center={defaultCenter}
          zoom={5}
          className="h-full w-full"
          style={{ height: '100%', width: '100%', background: '#18181b' }}
          zoomControl={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
          />
          {markers.length > 0 && <FitBounds positions={markers.map(m => m.coords)} userLocation={userLocation} />}
          {userLocation && (
            <>
              <Marker position={[userLocation.lat, userLocation.lng]} icon={userIcon}>
                <Popup>
                  <div style={{ fontFamily: '-apple-system, sans-serif', textAlign: 'center' }}>
                    <strong style={{ fontSize: '13px', color: '#3b82f6' }}>Your Location</strong>
                  </div>
                </Popup>
              </Marker>
              <Circle center={[userLocation.lat, userLocation.lng]} radius={16093} pathOptions={{ color: '#3b82f6', fillColor: '#3b82f6', fillOpacity: 0.05, weight: 1, dashArray: '4 4' }} />
            </>
          )}
          {markers.map(gym => (
            <Marker
              key={gym.id}
              position={[gym.coords.lat, gym.coords.lng]}
              icon={gym.is_hyrox_affiliated ? hyroxIcon : gymIcon}
            >
              <Popup>
                <div style={{ minWidth: '200px', fontFamily: '-apple-system, sans-serif' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                    <strong style={{ fontSize: '14px', color: '#111' }}>{gym.gym_name}</strong>
                    {gym.is_hyrox_affiliated && (
                      <span style={{ fontSize: '9px', fontWeight: 800, background: '#c8ff00', color: '#000', padding: '1px 5px', letterSpacing: '1px' }}>HYROX</span>
                    )}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                    {gym.address ? `${gym.address}, ` : ''}{gym.city}, {gym.state}
                  </div>
                  {gym.membership_price && (
                    <div style={{ fontSize: '11px', color: '#888', marginBottom: '2px' }}>Membership: {gym.membership_price}</div>
                  )}
                  {gym.drop_in_price && (
                    <div style={{ fontSize: '11px', color: '#888', marginBottom: '4px' }}>Drop-in: {gym.drop_in_price}</div>
                  )}
                  {gym.website && (
                    <a href={gym.website} target="_blank" rel="noopener noreferrer"
                      style={{ fontSize: '11px', color: '#E63946', textDecoration: 'none' }}>
                      Visit Website &rarr;
                    </a>
                  )}
                  <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #eee' }}>
                    <a href={getDirectionsUrl(gym, userLocation)} target="_blank" rel="noopener noreferrer"
                      style={{ fontSize: '12px', color: '#000', fontWeight: 700, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#c8ff00', padding: '4px 10px' }}
                      data-testid={`map-directions-${gym.id}`}>
                      &#9654; Get Directions
                    </a>
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
      {geocoding && markers.length > 0 && (
        <div className="flex items-center gap-2 mt-2 text-xs text-zinc-600">
          <Clock className="w-3 h-3 animate-spin" /> Loading remaining locations...
        </div>
      )}
      {!geocoding && markers.length < gyms.length && (
        <p className="text-xs text-zinc-600 mt-2">
          {markers.length} of {gyms.length} gyms mapped (some addresses could not be geocoded)
        </p>
      )}
      <div className="flex items-center gap-4 mt-3 text-xs text-zinc-600">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-[#c8ff00] border border-zinc-700" />
          <span>HYROX Affiliated</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-[#E63946] border border-zinc-700" />
          <span>Training Gym</span>
        </div>
        {userLocation && (
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full bg-[#3b82f6] border-2 border-white shadow-[0_0_6px_rgba(59,130,246,0.5)]" />
            <span>You</span>
          </div>
        )}
      </div>
    </div>
  );
}

function GymCard({ gym, distance, userLocation, isCompared, onToggleCompare, compareDisabled }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div
      className={`border bg-zinc-900/60 hover:border-zinc-700 transition-all duration-300 ${isCompared ? 'border-[#c8ff00]/60 shadow-[0_0_12px_rgba(200,255,0,0.08)]' : 'border-zinc-800'}`}
      data-testid={`directory-gym-${gym.id}`}
    >
      <button onClick={() => setExpanded(!expanded)} className="w-full text-left p-5 flex items-start gap-4">
        <div className="w-11 h-11 bg-[#E63946]/10 border border-[#E63946]/30 flex items-center justify-center flex-shrink-0">
          <Dumbbell className="w-5 h-5 text-[#E63946]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-white font-bold text-base">{gym.gym_name}</h3>
            {gym.is_hyrox_affiliated && (
              <span className="px-1.5 py-0.5 text-[10px] font-bold tracking-wider uppercase bg-[#c8ff00]/10 text-[#c8ff00] border border-[#c8ff00]/20">HYROX</span>
            )}
            {distance !== null && distance !== undefined && (
              <span className="px-2 py-0.5 text-[10px] font-bold tracking-wider bg-[#3b82f6]/10 text-[#3b82f6] border border-[#3b82f6]/20" data-testid={`gym-distance-${gym.id}`}>
                {distance < 1 ? `${(distance * 5280).toFixed(0)} ft` : `${distance.toFixed(1)} mi`}
              </span>
            )}
          </div>
          <p className="text-zinc-500 text-sm mt-1 flex items-center gap-1">
            <MapPin className="w-3 h-3 flex-shrink-0" />
            {gym.address ? `${gym.address}, ` : ''}{gym.city}, {gym.state} {gym.zip_code || ''}
          </p>
        </div>
        <div className="flex-shrink-0 mt-1">
          {expanded ? <ChevronUp className="w-5 h-5 text-[#E63946]" /> : <ChevronDown className="w-5 h-5 text-zinc-600" />}
        </div>
      </button>

      {expanded && (
        <div className="px-5 pb-5 space-y-4 border-t border-zinc-800 pt-4">
          {gym.description && <p className="text-zinc-400 text-sm leading-relaxed">{gym.description}</p>}

          <div className="grid grid-cols-2 gap-3">
            {gym.membership_price && (
              <div className="bg-zinc-800/50 p-3">
                <div className="flex items-center gap-1.5 mb-1"><DollarSign className="w-3 h-3 text-[#c8ff00]" /><span className="text-zinc-500 text-[10px] uppercase tracking-wider">Membership</span></div>
                <p className="text-white text-sm font-medium">{gym.membership_price}</p>
              </div>
            )}
            {gym.drop_in_price && (
              <div className="bg-zinc-800/50 p-3">
                <div className="flex items-center gap-1.5 mb-1"><DollarSign className="w-3 h-3 text-[#E63946]" /><span className="text-zinc-500 text-[10px] uppercase tracking-wider">Drop-In</span></div>
                <p className="text-white text-sm font-medium">{gym.drop_in_price}</p>
              </div>
            )}
          </div>

          {gym.hyrox_class_schedule && (
            <div className="bg-zinc-800/30 p-3 border border-zinc-800">
              <div className="flex items-center gap-1.5 mb-2"><Calendar className="w-3 h-3 text-[#00F0FF]" /><span className="text-zinc-500 text-[10px] uppercase tracking-wider">HYROX Class Schedule</span></div>
              <p className="text-zinc-300 text-sm whitespace-pre-line">{gym.hyrox_class_schedule}</p>
            </div>
          )}

          {gym.class_types?.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {gym.class_types.map(c => (
                <span key={c} className="px-2 py-0.5 text-[10px] font-medium bg-zinc-800 text-zinc-400 border border-zinc-700">{c}</span>
              ))}
            </div>
          )}

          {gym.amenities?.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {gym.amenities.map(a => (
                <span key={a} className="px-2 py-0.5 text-[10px] font-medium bg-[#E63946]/5 text-[#E63946]/80 border border-[#E63946]/10">{a}</span>
              ))}
            </div>
          )}

          <div className="flex gap-3 pt-2 flex-wrap items-center justify-between">
            <div className="flex gap-3 flex-wrap">
              <a href={getDirectionsUrl(gym, userLocation)} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-xs font-semibold text-[#c8ff00] hover:underline"
                data-testid={`directions-${gym.id}`}>
                <Navigation className="w-3 h-3" /> Get Directions
              </a>
              {gym.website && (
                <a href={gym.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-xs text-[#00F0FF] hover:underline">
                  <Globe className="w-3 h-3" /> Website
                </a>
              )}
              {gym.instagram && (
                <a href={`https://instagram.com/${gym.instagram.replace('@','')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-xs text-[#E63946] hover:underline">
                  <Instagram className="w-3 h-3" /> {gym.instagram}
                </a>
              )}
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); onToggleCompare(gym); }}
              disabled={compareDisabled && !isCompared}
              className={`flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-3 py-1.5 border transition-all ${
                isCompared
                  ? 'border-[#c8ff00] bg-[#c8ff00]/10 text-[#c8ff00]'
                  : compareDisabled
                  ? 'border-zinc-800 text-zinc-700 cursor-not-allowed'
                  : 'border-zinc-700 text-zinc-400 hover:border-[#c8ff00] hover:text-[#c8ff00]'
              }`}
              data-testid={`compare-btn-${gym.id}`}
            >
              <GitCompare className="w-3 h-3" />
              {isCompared ? 'Added' : 'Compare'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Sticky compare bar shown when gyms are selected
function GymCompareBar({ gyms, compareIds, onRemove, onCompare, onClear, onShare, copiedLink }) {
  if (compareIds.length === 0) return null;
  const selected = gyms.filter(g => compareIds.includes(g.id));
  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 bg-[#0f0f0f] border-t border-[#c8ff00]/30 shadow-[0_-4px_32px_rgba(200,255,0,0.1)]"
      data-testid="compare-bar"
    >
      <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-4 flex-wrap sm:flex-nowrap">
        <div className="flex items-center gap-2 flex-shrink-0">
          <GitCompare className="w-4 h-4 text-[#c8ff00]" />
          <span className="text-[#c8ff00] text-xs font-bold uppercase tracking-wider">
            Compare ({compareIds.length}/3)
          </span>
        </div>

        <div className="flex items-center gap-2 flex-1 flex-wrap">
          {selected.map(gym => (
            <div key={gym.id} className="flex items-center gap-1.5 bg-zinc-900 border border-[#c8ff00]/20 px-3 py-1.5 text-xs text-white" data-testid={`compare-bar-gym-${gym.id}`}>
              <span className="font-medium truncate max-w-[120px]">{gym.gym_name}</span>
              <span className="text-zinc-500">{gym.city}, {gym.state}</span>
              <button onClick={() => onRemove(gym.id)} className="text-zinc-600 hover:text-[#E63946] ml-1 transition-colors" data-testid={`compare-remove-${gym.id}`}>
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          {Array.from({ length: 3 - selected.length }, (_, idx) => `placeholder-${idx}`).map(k => (
            <div key={k} className="flex items-center gap-1.5 border border-dashed border-zinc-800 px-3 py-1.5 text-xs text-zinc-700">
              <Minus className="w-3 h-3" /> Add gym
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
          <button onClick={onClear} className="text-xs text-zinc-600 hover:text-zinc-400 uppercase tracking-wider transition-colors" data-testid="compare-bar-clear">
            Clear
          </button>
          <button
            onClick={onShare}
            disabled={compareIds.length < 2}
            className="hidden sm:flex items-center gap-1.5 px-3 py-2 border border-zinc-700 text-zinc-400 text-xs font-bold uppercase tracking-wider hover:border-[#c8ff00] hover:text-[#c8ff00] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            data-testid="compare-share-btn"
            title="Copy shareable comparison link"
          >
            {copiedLink ? <><Check className="w-3.5 h-3.5 text-[#c8ff00]" /> Copied!</> : <><Share2 className="w-3.5 h-3.5" /> Share</>}
          </button>
          <button
            onClick={onCompare}
            disabled={compareIds.length < 2}
            className="px-5 py-2 bg-[#c8ff00] text-black text-xs font-bold uppercase tracking-wider hover:bg-[#d4ff33] disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
            data-testid="compare-bar-btn"
          >
            <GitCompare className="w-3.5 h-3.5" />
            Compare Now
          </button>
        </div>
      </div>
    </div>
  );
}

// Full side-by-side comparison modal
function GymCompareModal({ gyms, compareIds, gymDistances, userLocation, onClose, onShare, copiedLink }) {
  const selected = gyms.filter(g => compareIds.includes(g.id));

  const Row = ({ label, render, accent }) => (
    <tr className={`border-b border-zinc-800/50 ${accent ? 'bg-zinc-900/40' : ''}`}>
      <td className="py-3 pr-4 text-zinc-500 text-xs font-medium uppercase tracking-wider whitespace-nowrap align-top" style={{ width: '9rem' }}>{label}</td>
      {selected.map(gym => (
        <td key={gym.id} className="py-3 px-4 text-sm text-white align-top">{render(gym)}</td>
      ))}
    </tr>
  );

  return (
    <div className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-sm flex flex-col overflow-hidden" data-testid="compare-modal">
      <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-800 bg-[#0a0a0a] flex-shrink-0">
        <div className="flex items-center gap-3">
          <GitCompare className="w-5 h-5 text-[#c8ff00]" />
          <h2 className="text-white font-bold text-lg">Gym Comparison</h2>
          <span className="text-zinc-600 text-sm">{selected.length} gyms selected</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onShare}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-zinc-800 text-zinc-500 hover:border-[#c8ff00] hover:text-[#c8ff00] text-xs font-bold uppercase tracking-wider transition-colors"
            data-testid="compare-modal-share-btn"
            title="Copy shareable link to this comparison"
          >
            {copiedLink ? <><Check className="w-3.5 h-3.5 text-[#c8ff00]" /> Copied!</> : <><Link2 className="w-3.5 h-3.5" /> Copy Link</>}
          </button>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center border border-zinc-800 text-zinc-500 hover:text-white hover:border-zinc-600 transition-colors" data-testid="compare-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        <div className="min-w-[560px] px-6 py-6">
          {/* Gym header cards */}
          <div className="flex gap-4 mb-8" style={{ paddingLeft: '9rem' }}>
            {selected.map((gym, idx) => (
              <div key={gym.id} className="flex-1 bg-zinc-900 border border-zinc-800 p-4 relative" data-testid={`compare-col-${gym.id}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <h3 className="text-white font-bold text-base leading-tight">{gym.gym_name}</h3>
                    <p className="text-zinc-500 text-xs mt-1 flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {gym.city}, {gym.state}
                    </p>
                  </div>
                  {gym.is_hyrox_affiliated && (
                    <span className="text-[9px] font-black bg-[#c8ff00] text-black px-1.5 py-0.5 uppercase tracking-wider flex-shrink-0">HYROX</span>
                  )}
                </div>
                {gymDistances[gym.id] != null && (
                  <span className="inline-block text-[10px] bg-[#3b82f6]/10 text-[#3b82f6] border border-[#3b82f6]/20 px-2 py-0.5 font-bold mt-1">
                    {gymDistances[gym.id] < 1 ? `${(gymDistances[gym.id] * 5280).toFixed(0)} ft away` : `${gymDistances[gym.id].toFixed(1)} mi away`}
                  </span>
                )}
                <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-[#E63946] text-white text-[10px] font-black flex items-center justify-center">
                  {idx + 1}
                </div>
              </div>
            ))}
          </div>

          <table className="w-full border-separate border-spacing-0">
            <colgroup>
              <col style={{ width: '9rem' }} />
              {selected.map(g => <col key={g.id} />)}
            </colgroup>
            <tbody>
              <tr><td colSpan={selected.length + 1} className="py-2 text-[10px] text-zinc-600 uppercase tracking-widest font-bold">Pricing</td></tr>
              <Row label="Membership" render={gym => gym.membership_price
                ? <span className="text-[#c8ff00] font-semibold">{gym.membership_price}</span>
                : <span className="text-zinc-700">—</span>}
              />
              <Row label="Drop-In" render={gym => gym.drop_in_price
                ? <span className="text-[#c8ff00] font-semibold">{gym.drop_in_price}</span>
                : <span className="text-zinc-700">—</span>}
              />
              <tr><td colSpan={selected.length + 1} className="pt-6 pb-2 text-[10px] text-zinc-600 uppercase tracking-widest font-bold">General</td></tr>
              <Row label="HYROX Gym" render={gym => gym.is_hyrox_affiliated
                ? <span className="flex items-center gap-1 text-[#c8ff00] font-semibold"><Check className="w-3.5 h-3.5" /> Yes</span>
                : <span className="text-zinc-600">No</span>}
              />
              <Row label="Schedule" render={gym => gym.hyrox_class_schedule
                ? <span className="text-zinc-300 text-xs leading-relaxed">{gym.hyrox_class_schedule}</span>
                : <span className="text-zinc-700">—</span>}
              />
              <Row label="Website" render={gym => gym.website
                ? <a href={gym.website} target="_blank" rel="noopener noreferrer" className="text-[#00F0FF] hover:underline text-xs flex items-center gap-1"><Globe className="w-3 h-3" /> Visit</a>
                : <span className="text-zinc-700">—</span>}
              />
              <Row label="Directions" render={gym => (
                <a href={getDirectionsUrl(gym, userLocation)} target="_blank" rel="noopener noreferrer"
                  className="text-[#c8ff00] hover:underline text-xs flex items-center gap-1" data-testid={`compare-directions-${gym.id}`}>
                  <Navigation className="w-3 h-3" /> Get Directions
                </a>
              )} />

              {CLASS_OPTIONS.filter(c => selected.some(g => g.class_types?.includes(c))).length > 0 && (
                <>
                  <tr><td colSpan={selected.length + 1} className="pt-6 pb-2 text-[10px] text-zinc-600 uppercase tracking-widest font-bold">Class Types</td></tr>
                  {CLASS_OPTIONS.filter(c => selected.some(g => g.class_types?.includes(c))).map(cls => (
                    <Row key={cls} label={cls} accent render={gym => gym.class_types?.includes(cls)
                      ? <Check className="w-4 h-4 text-[#c8ff00]" />
                      : <Minus className="w-4 h-4 text-zinc-800" />}
                    />
                  ))}
                </>
              )}

              {AMENITY_OPTIONS.filter(a => selected.some(g => g.amenities?.includes(a))).length > 0 && (
                <>
                  <tr><td colSpan={selected.length + 1} className="pt-6 pb-2 text-[10px] text-zinc-600 uppercase tracking-widest font-bold">Amenities & Equipment</td></tr>
                  {AMENITY_OPTIONS.filter(a => selected.some(g => g.amenities?.includes(a))).map(am => (
                    <Row key={am} label={am} accent render={gym => gym.amenities?.includes(am)
                      ? <Check className="w-4 h-4 text-[#E63946]" />
                      : <Minus className="w-4 h-4 text-zinc-800" />}
                    />
                  ))}
                </>
              )}
            </tbody>
          </table>
          <div className="mt-8 pb-8" />
        </div>
      </div>
    </div>
  );
}

function SubmissionForm({ onSuccess }) {
  const [form, setForm] = useState({
    contact_name: '', contact_email: '', phone: '', website: '', instagram: '',
    description: '', is_hyrox_affiliated: false,
    hyrox_class_schedule: '', membership_price: '', drop_in_price: '',
    amenities: [], class_types: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/gym-submissions`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || 'Submission failed'); }
      setSubmitted(true);
      onSuccess?.();
    } catch (err) { setError(err.message); }
    finally { setSubmitting(false); }
  };

  const toggleArray = (key, val) => {
    setForm(f => ({
      ...f,
      [key]: f[key].includes(val) ? f[key].filter(v => v !== val) : [...f[key], val]
    }));
  };

  if (submitted) {
    return (
      <div className="text-center py-16" data-testid="submission-success">
        <CheckCircle className="w-12 h-12 text-[#c8ff00] mx-auto mb-4" />
        <h3 className="text-white text-xl font-bold mb-2">Submission Received</h3>
        <p className="text-zinc-400 text-sm max-w-md mx-auto">
          Your gym will be reviewed by our team and added to the directory once approved. You'll receive an email confirmation.
        </p>
      </div>
    );
  }

  const Input = ({ label, name, required, type = 'text', placeholder }) => (
    <div>
      <label className="block text-zinc-400 text-xs font-medium uppercase tracking-wider mb-1.5">{label} {required && <span className="text-[#E63946]">*</span>}</label>
      <input
        type={type} value={form[name]} onChange={e => setForm(f => ({ ...f, [name]: e.target.value }))}
        required={required} placeholder={placeholder}
        className="w-full bg-zinc-900 border border-zinc-800 text-white px-3 py-2.5 text-sm focus:border-[#E63946] focus:outline-none transition-colors placeholder:text-zinc-700"
        data-testid={`gym-form-${name}`}
      />
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="gym-submission-form">
      <div className="space-y-1">
        <h3 className="text-white font-bold text-lg">List Your Gym</h3>
        <p className="text-zinc-500 text-sm">Fill out the details below. Submissions are reviewed before being listed.</p>
      </div>

      {error && <div className="bg-red-900/20 border border-red-800 text-red-400 text-sm px-4 py-3">{error}</div>}

      <div className="space-y-4">
        <p className="text-[#c8ff00] text-xs font-bold uppercase tracking-wider">Gym Details</p>
        <div className="grid md:grid-cols-2 gap-4">
          <Input label="Gym Name" name="gym_name" required placeholder="e.g. FitLab Dallas" />
          <Input label="Website" name="website" placeholder="https://..." />
        </div>
        <Input label="Address" name="address" placeholder="123 Main St" />
        <div className="grid grid-cols-3 gap-4">
          <Input label="City" name="city" required placeholder="Dallas" />
          <Input label="State" name="state" required placeholder="TX" />
          <Input label="ZIP" name="zip_code" placeholder="75201" />
        </div>
        <Input label="Instagram" name="instagram" placeholder="@yourgym" />
      </div>

      <div className="space-y-4">
        <p className="text-[#c8ff00] text-xs font-bold uppercase tracking-wider">Contact Info</p>
        <div className="grid md:grid-cols-2 gap-4">
          <Input label="Contact Name" name="contact_name" required placeholder="Full name" />
          <Input label="Contact Email" name="contact_email" required type="email" placeholder="you@gym.com" />
        </div>
        <Input label="Phone" name="phone" placeholder="+1 (555) 123-4567" />
      </div>

      <div className="space-y-4">
        <p className="text-[#c8ff00] text-xs font-bold uppercase tracking-wider">HYROX & Classes</p>
        <label className="flex items-center gap-3 cursor-pointer" data-testid="gym-form-hyrox-toggle">
          <div className={`w-10 h-5 rounded-full relative transition-colors ${form.is_hyrox_affiliated ? 'bg-[#c8ff00]' : 'bg-zinc-700'}`}
            onClick={() => setForm(f => ({ ...f, is_hyrox_affiliated: !f.is_hyrox_affiliated }))}>
            <div className={`w-4 h-4 rounded-full bg-white absolute top-0.5 transition-transform ${form.is_hyrox_affiliated ? 'translate-x-5' : 'translate-x-0.5'}`} />
          </div>
          <span className="text-zinc-300 text-sm">Official HYROX Affiliated Gym</span>
        </label>

        <div>
          <label className="block text-zinc-400 text-xs font-medium uppercase tracking-wider mb-2">Class Types</label>
          <div className="flex flex-wrap gap-2">
            {CLASS_OPTIONS.map(c => (
              <button key={c} type="button" onClick={() => toggleArray('class_types', c)}
                className={`px-3 py-1.5 text-xs font-medium border transition-colors ${form.class_types.includes(c)
                  ? 'border-[#E63946] bg-[#E63946]/10 text-[#E63946]'
                  : 'border-zinc-800 text-zinc-500 hover:border-zinc-600'}`}>
                {c}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-zinc-400 text-xs font-medium uppercase tracking-wider mb-1.5">HYROX Class Schedule</label>
          <textarea value={form.hyrox_class_schedule} onChange={e => setForm(f => ({ ...f, hyrox_class_schedule: e.target.value }))}
            className="w-full bg-zinc-900 border border-zinc-800 text-white px-3 py-2.5 text-sm focus:border-[#E63946] focus:outline-none h-20 resize-none placeholder:text-zinc-700"
            placeholder="e.g. Mon/Wed/Fri 6:00 AM, Sat 9:00 AM" data-testid="gym-form-schedule" />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <Input label="Membership Price" name="membership_price" placeholder="$150/mo" />
          <Input label="Drop-In Price" name="drop_in_price" placeholder="$25/session" />
        </div>
      </div>

      <div className="space-y-4">
        <p className="text-[#c8ff00] text-xs font-bold uppercase tracking-wider">Amenities & Equipment</p>
        <div className="flex flex-wrap gap-2">
          {AMENITY_OPTIONS.map(a => (
            <button key={a} type="button" onClick={() => toggleArray('amenities', a)}
              className={`px-3 py-1.5 text-xs font-medium border transition-colors ${form.amenities.includes(a)
                ? 'border-[#c8ff00] bg-[#c8ff00]/10 text-[#c8ff00]'
                : 'border-zinc-800 text-zinc-500 hover:border-zinc-600'}`}>
              {a}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-zinc-400 text-xs font-medium uppercase tracking-wider mb-1.5">Description</label>
        <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          className="w-full bg-zinc-900 border border-zinc-800 text-white px-3 py-2.5 text-sm focus:border-[#E63946] focus:outline-none h-24 resize-none placeholder:text-zinc-700"
          placeholder="What makes your gym great for HYROX training?" data-testid="gym-form-description" />
      </div>

      <button type="submit" disabled={submitting}
        className="w-full bg-[#E63946] text-white py-3 font-bold text-sm uppercase tracking-wider hover:bg-[#c41e3a] disabled:opacity-50 flex items-center justify-center gap-2"
        data-testid="gym-form-submit">
        {submitting ? <Clock className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        {submitting ? 'Submitting...' : 'Submit for Review'}
      </button>
    </form>
  );
}

export default function GymDirectoryPage() {
  const [gyms, setGyms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'map'
  const [searchQuery, setSearchQuery] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [userLocation, setUserLocation] = useState(null);
  const [locating, setLocating] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [gymDistances, setGymDistances] = useState({});
  const [compareIds, setCompareIds] = useState([]);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  // Parse ?compare= from URL on mount
  const urlCompareIds = useRef(
    new URLSearchParams(window.location.search).get('compare')?.split(',').filter(Boolean) || []
  );

  // Sync compareIds → URL (replaceState, no navigation)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (compareIds.length > 0) {
      params.set('compare', compareIds.join(','));
    } else {
      params.delete('compare');
    }
    const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
    window.history.replaceState(null, '', newUrl);
  }, [compareIds]);

  // After gyms load, apply ?compare= IDs from URL
  useEffect(() => {
    if (gyms.length === 0 || urlCompareIds.current.length === 0) return;
    const valid = urlCompareIds.current.filter(id => gyms.some(g => g.id === id));
    if (valid.length >= 2) {
      setCompareIds(valid.slice(0, 3));
      setShowCompareModal(true);
      urlCompareIds.current = []; // consume once
    }
  }, [gyms]);

  const handleShareComparison = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchGyms(); }, [stateFilter]);

  const fetchGyms = async () => {
    try {
      const params = new URLSearchParams();
      if (stateFilter) params.set('state', stateFilter);
      const res = await fetch(`${API_URL}/api/gym-directory?${params}`);
      const data = await res.json();
      setGyms(data);
    } catch { }
    finally { setLoading(false); }
  };

  // Calculate distances when user location or gyms change
  useEffect(() => {
    if (!userLocation || gyms.length === 0) return;
    async function calcDistances() {
      const distances = {};
      for (const gym of gyms) {
        const coords = await geocodeAddress(gym);
        if (coords) {
          distances[gym.id] = haversine(userLocation.lat, userLocation.lng, coords.lat, coords.lng);
        }
      }
      setGymDistances(distances);
    }
    calcDistances();
  }, [userLocation, gyms]);

  const requestLocation = () => {
    setLocating(true);
    setLocationError('');
    if (!navigator.geolocation) {
      setLocationError('Geolocation not supported by your browser');
      setLocating(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocating(false);
      },
      (err) => {
        setLocationError(err.code === 1 ? 'Location access denied. Enable it in browser settings.' : 'Could not get your location. Try again.');
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const clearLocation = () => {
    setUserLocation(null);
    setGymDistances({});
  };

  const toggleCompare = (gym) => {
    setCompareIds(prev => {
      if (prev.includes(gym.id)) return prev.filter(id => id !== gym.id);
      if (prev.length >= 3) return prev;
      return [...prev, gym.id];
    });
  };

  const filtered = gyms.filter(g => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return g.gym_name?.toLowerCase().includes(q) || g.city?.toLowerCase().includes(q) || g.state?.toLowerCase().includes(q);
  });

  // Sort by distance if user location is available
  const sortedGyms = userLocation && Object.keys(gymDistances).length > 0
    ? [...filtered].sort((a, b) => (gymDistances[a.id] ?? Infinity) - (gymDistances[b.id] ?? Infinity))
    : filtered;

  const states = [...new Set(gyms.map(g => g.state))].sort();

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="gym-directory-page">
      {/* Hero */}
      <div className="relative bg-gradient-to-b from-zinc-900 to-[#0a0a0a] border-b border-zinc-800">
        <div className="max-w-5xl mx-auto px-4 py-16 sm:py-20">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-1.5 h-8 bg-[#E63946]" />
            <span className="text-[#c8ff00] text-xs font-bold uppercase tracking-[4px]">HYROX Training</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight leading-tight mb-4">
            Gym <span className="text-[#E63946]">Directory</span>
          </h1>
          <p className="text-zinc-400 text-base sm:text-lg max-w-xl leading-relaxed">
            Find HYROX-ready gyms near you. Every gym listed has been verified by our team for race-day prep capabilities.
            {' '}Prefer a quick local search? <Link to="/hyrox/near-me" className="text-[#c8ff00] hover:underline" data-testid="directory-near-me-link">Try HYROX Near Me →</Link>
          </p>

          <div className="flex flex-wrap gap-3 mt-8">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search gyms or cities..."
                className="w-full bg-zinc-900 border border-zinc-800 text-white pl-10 pr-4 py-2.5 text-sm focus:border-[#E63946] focus:outline-none placeholder:text-zinc-700"
                data-testid="gym-search-input" />
            </div>
            {states.length > 0 && (
              <select value={stateFilter} onChange={e => setStateFilter(e.target.value)}
                className="bg-zinc-900 border border-zinc-800 text-zinc-400 px-4 py-2.5 text-sm focus:border-[#E63946] focus:outline-none"
                data-testid="gym-state-filter">
                <option value="">All States</option>
                {states.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            )}
            <button onClick={() => setShowForm(!showForm)}
              className={`px-5 py-2.5 text-xs font-bold uppercase tracking-wider flex items-center gap-2 transition-colors ${showForm ? 'bg-zinc-800 text-zinc-300' : 'bg-[#c8ff00] text-black hover:bg-[#d4ff33]'}`}
              data-testid="toggle-gym-form-btn">
              <Dumbbell className="w-4 h-4" />
              {showForm ? 'View Directory' : 'List Your Gym'}
            </button>
            {!showForm && (
              userLocation ? (
                <button onClick={clearLocation}
                  className="px-5 py-2.5 text-xs font-bold uppercase tracking-wider flex items-center gap-2 bg-[#3b82f6] text-white hover:bg-[#2563eb] transition-colors"
                  data-testid="clear-location-btn">
                  <Crosshair className="w-4 h-4" /> Near Me
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                </button>
              ) : (
                <button onClick={requestLocation} disabled={locating}
                  className="px-5 py-2.5 text-xs font-bold uppercase tracking-wider flex items-center gap-2 bg-zinc-800 text-zinc-300 border border-zinc-700 hover:border-[#3b82f6] hover:text-[#3b82f6] disabled:opacity-50 transition-colors"
                  data-testid="near-me-btn">
                  {locating ? <Loader className="w-4 h-4 animate-spin" /> : <Crosshair className="w-4 h-4" />}
                  {locating ? 'Locating...' : 'Near Me'}
                </button>
              )
            )}
          </div>
          {locationError && (
            <p className="mt-3 text-xs text-red-400/80" data-testid="location-error">{locationError}</p>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 py-10">
        {showForm ? (
          <div className="max-w-2xl mx-auto bg-zinc-900/40 border border-zinc-800 p-6 sm:p-8">
            <SubmissionForm onSuccess={() => { setShowForm(false); fetchGyms(); }} />
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <p className="text-zinc-500 text-sm">{sortedGyms.length} gym{sortedGyms.length !== 1 ? 's' : ''} listed</p>
                {userLocation && Object.keys(gymDistances).length > 0 && (
                  <span className="text-[10px] text-[#3b82f6] bg-[#3b82f6]/10 px-2 py-0.5 border border-[#3b82f6]/20 font-medium" data-testid="sorted-by-distance">
                    Sorted by distance
                  </span>
                )}
              </div>
              {sortedGyms.length > 0 && (
                <div className="flex bg-zinc-900 border border-zinc-800 p-0.5" data-testid="view-mode-toggle">
                  <button
                    onClick={() => setViewMode('list')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold transition-colors ${viewMode === 'list' ? 'bg-[#E63946] text-white' : 'text-zinc-500 hover:text-zinc-300'}`}
                    data-testid="view-list-btn"
                  >
                    <List className="w-3.5 h-3.5" /> List
                  </button>
                  <button
                    onClick={() => setViewMode('map')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold transition-colors ${viewMode === 'map' ? 'bg-[#E63946] text-white' : 'text-zinc-500 hover:text-zinc-300'}`}
                    data-testid="view-map-btn"
                  >
                    <Map className="w-3.5 h-3.5" /> Map
                  </button>
                </div>
              )}
            </div>

            {loading ? (
              <div className="text-center py-20"><Clock className="w-6 h-6 text-zinc-600 animate-spin mx-auto" /></div>
            ) : sortedGyms.length === 0 ? (
              <div className="text-center py-20 border border-dashed border-zinc-800" data-testid="no-gyms-message">
                <Dumbbell className="w-10 h-10 text-zinc-700 mx-auto mb-4" />
                <h3 className="text-zinc-400 font-bold mb-2">No Gyms Listed Yet</h3>
                <p className="text-zinc-600 text-sm mb-6 max-w-md mx-auto">
                  Be the first to add a HYROX-ready gym in your area.
                </p>
                <button onClick={() => setShowForm(true)}
                  className="px-6 py-2.5 bg-[#E63946] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#c41e3a]"
                  data-testid="list-gym-cta">
                  List Your Gym
                </button>
              </div>
            ) : viewMode === 'map' ? (
              <GymMapView gyms={sortedGyms} userLocation={userLocation} />
            ) : (
              <div className={`space-y-3 ${compareIds.length > 0 ? 'pb-24' : ''}`}>
                {sortedGyms.map(gym => (
                  <GymCard
                    key={gym.id}
                    gym={gym}
                    distance={gymDistances[gym.id]}
                    userLocation={userLocation}
                    isCompared={compareIds.includes(gym.id)}
                    onToggleCompare={toggleCompare}
                    compareDisabled={compareIds.length >= 3}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Compare Bar */}
      <GymCompareBar
        gyms={gyms}
        compareIds={compareIds}
        onRemove={(id) => setCompareIds(prev => prev.filter(x => x !== id))}
        onCompare={() => setShowCompareModal(true)}
        onClear={() => setCompareIds([])}
        onShare={handleShareComparison}
        copiedLink={copiedLink}
      />

      {/* Compare Modal */}
      {showCompareModal && (
        <GymCompareModal
          gyms={gyms}
          compareIds={compareIds}
          gymDistances={gymDistances}
          userLocation={userLocation}
          onClose={() => setShowCompareModal(false)}
          onShare={handleShareComparison}
          copiedLink={copiedLink}
        />
      )}
    </div>
  );
}
